/**
 * usePaymentReturn Hook
 *
 * Handles return from Stripe Checkout.
 * Checks URL parameters and confirms payment with backend.
 *
 * IMPORTANT: Uses useRef guard to prevent double processing in React StrictMode
 * and during re-renders caused by query invalidation.
 */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmStripeCheckout } from '@/lib/api/payment';
import { toast } from 'sonner';

export interface UsePaymentReturnResult {
  isProcessing: boolean;
}

export function usePaymentReturn(onSuccess?: () => void): UsePaymentReturnResult {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  // Guard against double processing in React StrictMode
  // useRef is synchronous - second call sees true immediately
  const paymentProcessedRef = useRef(false);

  useEffect(() => {
    const handlePaymentReturn = async () => {
      const payment = searchParams.get('payment');
      const sessionId = searchParams.get('session_id');

      // Payment cancelled
      if (payment === 'cancelled') {
        toast.info(t('billing.paymentCancelled'));
        // Clean URL
        navigate('/v2/credits', { replace: true });
        return;
      }

      // Guard: Prevent double processing
      // Check BEFORE async operations to block StrictMode second invocation
      if (paymentProcessedRef.current) {
        return;
      }

      // Payment success
      if (payment === 'success' && sessionId) {
        // Set guard IMMEDIATELY (before async call)
        paymentProcessedRef.current = true;
        setIsProcessing(true);

        try {
          const result = await confirmStripeCheckout(sessionId);

          if (result.success) {
            toast.success(result.message || t('billing.paymentSuccess'));
            onSuccess?.();
          } else {
            toast.error(result.error || t('billing.paymentConfirmationError'));
          }
        } catch (err) {
          console.error('Payment confirmation error:', err);
          toast.error(t('billing.paymentConfirmationError'));
        } finally {
          setIsProcessing(false);
          // Clean URL
          navigate('/v2/credits', { replace: true });
        }
      }
    };

    handlePaymentReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // IMPORTANT: Empty deps - run only once on mount

  return {
    isProcessing,
  };
}
